export const GATEWAY_API_URL = import.meta.env['VITE_GATEWAY_API_URL'];
export const COSMOPOLITAN_URL = import.meta.env['VITE_COSMOPOLITAN_URL'] as string | undefined;
export const STYLIST_API_URL = import.meta.env['VITE_STYLIST_API_URL'];
export const CRM_ADMIN_API_URL = import.meta.env['VITE_CRM_ADMIN_API_URL'];
export const PORTAL_NAME = import.meta.env['VITE_PORTAL_NAME'] as
  | 'admin'
  | 'vendor'
  | 'warehouse'
  | 'synq'
  | 'logistics';
export const UNICOMMERCE_PORTAL_URL = import.meta.env['VITE_UNICOM_URL'];
export const APP_ENV = import.meta.env['VITE_APP_ENV'];
export const APP_MODE = import.meta.env.MODE;
export const SHOULD_HIDE_CUSTOMER_PHONE = PORTAL_NAME === 'vendor';
export const IS_LOCAL_ENV = APP_MODE === 'local' || APP_ENV === 'local';
export const IS_STAGE_ENV =
  APP_MODE === 'stage' || APP_MODE === 'staging' || APP_ENV === 'stage' || APP_ENV === 'staging';
export const IS_PROD_ENV =
  (APP_MODE === 'prod' || APP_MODE === 'production') &&
  (APP_ENV === 'prod' || APP_ENV === 'production');

// Explicit opt-in for running prod-only Foundation tools against local services.
export const LOCAL_DEVELOPER_FEATURES_ENABLED =
  IS_LOCAL_ENV && import.meta.env['VITE_ENABLE_LOCAL_DEVELOPER_FEATURES'] === 'true';
export const PRODUCTION_FEATURES_VISIBLE = IS_PROD_ENV || LOCAL_DEVELOPER_FEATURES_ENABLED;
export const LOCAL_GATEWAY_AUTH_BYPASS_ENABLED = LOCAL_DEVELOPER_FEATURES_ENABLED;

const parseOptionalNumber = (value: string | undefined) => {
  if (!value?.trim()) {
    return undefined;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

export const MTO_HOMEPAGE_ONE_VIEW_CARD_ID = parseOptionalNumber(
  import.meta.env['VITE_MTO_HOMEPAGE_ONE_VIEW_CARD_ID'],
);

/**
 * Where published skill bundles live: CloudFront distribution E3BBJUIA72ECYS, backed by
 * the dedicated skills bucket. The skills repo republishes these on every merge to main,
 * so they are served from their own origin rather than bundled with this app — an admin
 * deploy's `s3 sync --delete` would otherwise remove them.
 *
 * Bundles sit at the bucket root, so this carries no path segment.
 */
export const SKILLS_CDN_URL =
  (import.meta.env['VITE_SKILLS_CDN_URL'] as string | undefined) ??
  'https://d3oogiutwyitso.cloudfront.net';

export const DWARKA_FACILITY_ID =
  parseOptionalNumber(import.meta.env['VITE_DWARKA_FACILITY_ID']) ?? 1;
export const SAMALKHA_FACILITY_ID =
  parseOptionalNumber(import.meta.env['VITE_SAMALKHA_FACILITY_ID']) ?? 481;

/**
 * Hub gatepass creation/scanning is enabled for the production rollout facilities:
 * Ambience VK Store (47), Ireo Store (83), Select Citywalk Store (102),
 * Khan Market Store (308), Samalkha RTV (480), Samalkha Warehouse (481), DLF Summit Store (487),
 * and Marol Dark Store (582).
 * Stage/local retain Samalkha Warehouse (481) and AMB VK Store (47),
 * the canonical stage test facility that holds the seeded gatepass/shelf test data,
 * Dwarka Warehouse (1) for warehouse picking tests, and Brigade Orion (116) for SCM store tests.
 */
export const GATEPASS_HUB_ENABLED_FACILITY_IDS: number[] = IS_PROD_ENV
  ? [47, 83, 102, 308, 480, 481, 487, 582]
  : [481, 47, 1, 116];

/**
 * Facilities SCM refuses on any stock transfer, as source or destination (Dwarka Warehouse 1, and 252).
 * Mirrors SCM's `lyskraft.stn.blocked-facility-ids` (the same 1,252 on every environment), so the Hub can
 * refuse before the picker fills anything in. SCM still enforces it.
 */
export const STN_BLOCKED_FACILITY_IDS: number[] = [1, 252];

// Manifests dispatched from the Samalkha Warehouse (facility 481, SAMALKHA) require RFID
// validation before Dispatch. Samalkha RTV (facility 480, SAMALKHA_RTV) is deliberately
// excluded -- only the warehouse needs the check.
// NOTE: 480 = Samalkha RTV, 481 = Samalkha Warehouse (per wms.facility). Earlier comments
// here had these swapped, which once inverted this gate; keep the ids labelled correctly.
export const RFID_VALIDATION_FACILITY_IDS: number[] = [481];

// Manifests travelling from or to these facilities are exempt from the RFID validation
// requirement. Currently none (Sector 7 Chandigarh Store, 665, was removed).
export const RFID_VALIDATION_EXEMPT_FACILITY_IDS: Array<number> = [];

export const getAppName = () => {
  switch (PORTAL_NAME) {
    case 'admin': {
      return 'Foundation';
    }
    case 'logistics': {
      return 'Logistics';
    }
    case 'synq': {
      return 'Synq';
    }
    case 'vendor': {
      return 'Nexus';
    }
    case 'warehouse': {
      return 'Hub';
    }

    default: {
      return 'Foundation';
    }
  }
};

/** Local development only: call the admin assistant service (admin-agent) directly instead of through the gateway. */
export const ADMIN_AGENT_URL = import.meta.env['VITE_ADMIN_AGENT_URL'] as string | undefined;
